import { Vector2 } from 'three';
import { Areas, LogicalSpace, Obstacle, Rectangle, Segment } from '@thive/fddm-types';

import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import {
  BoundingBox,
  CenteredBoundingBox,
  CenteredOrientedBoundingBox,
} from '@/modules/common/types/boundingBox';

type MinMaxBox = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

/**
 * @description calculates the bounding box extent (axis-aligned bb) bounds based of a rectangle with its position denoted by its center
 */
export function computeRectangleExtentBounds(
  centerX: number,
  centerY: number,
  width: number,
  length: number,
  angleInRadians: number,
): MinMaxBox {
  // calc the half diagonal
  const halfDiagonal = Math.sqrt((width / 2) ** 2 + (length / 2) ** 2);

  // corner angles relative to the rectangle
  const baseAngle = Math.atan2(length / 2, width / 2);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  // eslint-disable-next-line no-restricted-syntax
  for (const angle of [baseAngle, Math.PI - baseAngle, Math.PI + baseAngle, -baseAngle]) {
    const x = centerX + halfDiagonal * Math.cos(angleInRadians + angle);
    const y = centerY + halfDiagonal * Math.sin(angleInRadians + angle);

    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  return { minX, maxX, minY, maxY };
}

/**
 * given a collection of rectangles, computes and returns their axis-aligned bounding box
 */
export function computeRectanglesBoundingBoxExtent(rectangles: Rectangle[]): BoundingBox {
  const { minX, maxX, minY, maxY } = rectangles.reduce(
    (acc, { centerX, centerY, width, length, angle }) => {
      const minMax = computeRectangleExtentBounds(centerX, centerY, length, width, angle);

      if (minMax.minX < acc.minX) acc.minX = minMax.minX;
      if (minMax.minY < acc.minY) acc.minY = minMax.minY;
      if (minMax.maxX > acc.maxX) acc.maxX = minMax.maxX;
      if (minMax.maxY > acc.maxY) acc.maxY = minMax.maxY;

      return acc;
    },
    {
      minX: Infinity,
      maxX: -Infinity,
      minY: Infinity,
      maxY: -Infinity,
    },
  );

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * given a collection of rectangles, computes and returns their axis-aligned bounding box, with its position denoted by its center
 */
export function computeRectanglesCenterBoundingBox(rectangles: Rectangle[]): CenteredBoundingBox {
  const { minX, maxX, minY, maxY } = rectangles.reduce(
    (acc, { centerX, centerY, width, length, angle }) => {
      const minMax = computeRectangleExtentBounds(centerX, centerY, length, width, angle);

      if (minMax.minX < acc.minX) acc.minX = minMax.minX;
      if (minMax.minY < acc.minY) acc.minY = minMax.minY;
      if (minMax.maxX > acc.maxX) acc.maxX = minMax.maxX;
      if (minMax.maxY > acc.maxY) acc.maxY = minMax.maxY;

      return acc;
    },
    {
      minX: Infinity,
      maxX: -Infinity,
      minY: Infinity,
      maxY: -Infinity,
    },
  );

  const width = maxX - minX;
  const height = maxY - minY;

  return {
    centerX: minX + width / 2,
    centerY: minY + height / 2,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * given a collection of items, computes and returns their axis-aligned bounding box
 */
export function computeItemsBoundingBoxExtent(
  areas: Areas,
  obstacles: Obstacle[],
  logicalSpaces: LogicalSpace[],
): BoundingBox {
  const areaRectangles = RectangleUtils.convertAreasToRectangles(areas);
  const obstacleRectangles = obstacles.flatMap((item) =>
    item.segments.flatMap(RectangleUtils.fromSegment),
  );
  const logicalSpaceRectangles: Rectangle[] = [];
  logicalSpaces.forEach((logicalSpace) => {
    const rectangle = RectangleUtils.polygonToRectangle(logicalSpace.polygon);
    if (rectangle) logicalSpaceRectangles.push(rectangle);
  });

  return computeRectanglesBoundingBoxExtent([
    ...areaRectangles,
    ...obstacleRectangles,
    ...logicalSpaceRectangles,
  ]);
}

export const segmentToCenteredOrientedBoundingBox = (
  segment: Segment,
): CenteredOrientedBoundingBox => ({
  centerX: (segment.start.x + segment.end.x) / 2,
  centerY: (segment.start.y + segment.end.y) / 2,
  width: new Vector2(segment.start.x, segment.start.y).distanceTo(
    new Vector2(segment.end.x, segment.end.y),
  ),
  height: segment.leftSideWidth + segment.rightSideWidth,
  angle: Math.atan2(segment.end.y - segment.start.y, segment.end.x - segment.start.x),
});
